/*
 * Copyright (c) Logan Group Services - Gaston Longhitano <gaston.longhitano@logangroupservices.com>
 * All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React, {useEffect, useRef} from 'react';
import './Scene.scss';

import { FullScreen, useFullScreenHandle } from "react-full-screen";
import NavBar from './NavBar';
import {useRecoilValue, useRecoilState, useSetRecoilState} from 'recoil';
import {
  currentGameInfoQuery,
  currentSceneIDSelector,
  currentSceneInfoQuery,
} from '../recoil/selectors';
import {
  currentFragmentIDState,
  currentFullscreenModeState,
  currentSceneIDState,
  currentScreenState,
  currentVideoResolutionState,
  displayActionsState,
  displayControlsState,
  displayFragmentsState,
  displayNavigationState, isAutoPlayingState, isBufferingState,
  isPlayerReadyState,
  previousSceneIDState,
} from '../recoil/atoms';
import SceneSelection from './SceneSelection';
import {PLAYLIST_SCREEN, SELECTION_SCREEN} from '../recoil/state';
import {getImagePath, getVideoPath} from '../Helper';
import Fragments from './Fragments';
import {sign} from '../Api';
import Actions from './Actions';
import Controls from './Controls';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faPlay} from '@fortawesome/free-solid-svg-icons';
import Loader from '../elements/Loader/Loader';

function Scene() {
  const [showControls, setShowControls] = useRecoilState(displayControlsState);
  const [isFullScreen, setFullScreen] = useRecoilState(currentFullscreenModeState);
  const [currentScreen, setCurrentScreen] = useRecoilState(currentScreenState);
  const [currentFragmentID, setCurrentFragmentID] = useRecoilState(currentFragmentIDState);
  const [isPlayerReady, setIsPlayerReady] = useRecoilState(isPlayerReadyState);
  const [isAutoPlaying, setIsAutoPlaying] = useRecoilState(isAutoPlayingState);
  const [isBuffering, setIsBuffering] = useRecoilState(isBufferingState);

  const CurrentSceneID = useRecoilValue(currentSceneIDSelector);
  const currentScene = useRecoilValue(currentSceneInfoQuery);
  const currentGame = useRecoilValue(currentGameInfoQuery);
  const currentVideoResolution = useRecoilValue(currentVideoResolutionState);

  const setCurrentSceneID = useSetRecoilState(currentSceneIDState);
  const setPreviousSceneID = useSetRecoilState(previousSceneIDState);
  const setShowNavigation = useSetRecoilState(displayNavigationState);
  const setShowActions = useSetRecoilState(displayActionsState);
  const setShowFragments = useSetRecoilState(displayFragmentsState);

  const fullScreenHandler = useFullScreenHandle();

  const videoRef = useRef();
  const timersRef = useRef([]);

  useEffect(() => {
    setShowControls(true);
    setShowNavigation(true);
    setShowActions(true);
    setShowFragments(true);
    setIsBuffering(true);

    const timers = timersRef.current;
    const timer = setTimeout(() => {
      setShowControls(false);
      setShowNavigation(false);
      setShowActions(false);
      setShowFragments(false);
    }, 5000);

    timers.push(timer);

    return () => {
      timers.map((t) => clearTimeout(t));
      setShowNavigation(false);
      setIsPlayerReady(false);
      setShowActions(false);
      setShowFragments(false);
    }
  }, []);

  useEffect(() => {
    setCurrentFragmentID(0);
    handleFragmentPlay();
  }, [currentScene]);

  useEffect(() => {
    handleFragmentPlay()
  }, [currentFragmentID]);

  useEffect(() => {
    const currentTime = videoRef.current.currentTime;
    handleFragmentPlay();
    videoRef.current.currentTime = currentTime;
  }, [currentVideoResolution]);

  useEffect(() => {
    if(currentScreen === SELECTION_SCREEN) {
      videoRef.current.pause();
    }
  },[currentScreen]);

  const handlePlay = () => {
    setIsAutoPlaying(true);
    videoRef.current.play();
  };

  const handleEndOfVideo = () => {
    if(currentFragmentID < currentScene.fragments.length - 1) {
      setCurrentFragmentID(id => id + 1); // Play next fragment
    } else {

      if(currentScene.choices.length > 1) {
        setCurrentScreen(SELECTION_SCREEN);
      } else  {
        const nextScene = currentScene.choices[0].linkToScene || '';
        const firstScene = currentGame.tree.id || '';
        if (nextScene === firstScene) {
          setCurrentSceneID(null);
          setCurrentScreen(PLAYLIST_SCREEN);
          setIsPlayerReady(false);
        } else {
          setPreviousSceneID(CurrentSceneID);
          setCurrentSceneID(nextScene);
        }
      }
    }
  };

  const handleGoToSexScene = (e) => {
    e.preventDefault();
    setCurrentSceneID(currentScene.skipToSex);
  };

  const handleFragmentPlay = () => {
    videoRef.current.poster = currentScene &&
        currentScene.fragments[currentFragmentID] &&
        getImagePath(
            currentScene?.fragments[currentFragmentID]?.poster,
            currentGame.site, currentGame.shootId,
            currentScene?.fragments[currentFragmentID]?.fragmentId
        );

    videoRef.current.src = currentScene &&
        currentScene.fragments[currentFragmentID] &&
        sign(getVideoPath(
            currentScene?.fragments[currentFragmentID]?.resource,
            currentGame.site, currentGame.shootId,
            currentVideoResolution,
            currentScene?.fragments[currentFragmentID]?.fragmentId)
        );
  };

  const handleActionPlay = (id) => {
    videoRef.current.poster = currentScene &&
        currentScene.fragments[currentFragmentID] &&
        getImagePath(
            currentScene?.fragments[currentFragmentID]?.actions[id].poster,
            currentGame.site, currentGame.shootId,
            currentScene?.fragments[currentFragmentID]?.actions[id].fragmentId
        );
    videoRef.current.src = currentScene &&
        currentScene.fragments[currentFragmentID] &&
        sign(getVideoPath(
            currentScene?.fragments[currentFragmentID]?.actions[id].resource,
            currentGame.site, currentGame.shootId, currentVideoResolution,
            currentScene?.fragments[currentFragmentID]?.actions[id].fragmentId)
        );
  };

  // The video is ready
  const handleLoadedMetadata = () => {
    setIsPlayerReady(true);
    const promise = videoRef.current.play();
    if (promise !== undefined) {
      promise.then(_ => {
        //console.log('Autoplay started!');
        // const timerBuffering = setTimeout(() => {
        //   if (!videoRef.current.currentTime) {
        //     setIsBuffering(true);
        //   }
        // }, 1500);
      }).catch(error => {
         //console.log('Autoplay was prevented');
        // Show a "Play" button so that user can start playback.
        setIsAutoPlaying(false);
      });
    }
  };

  const handleFullscreen = () => {
    if(document.fullscreenEnabled !== undefined) {
      if(isFullScreen) fullScreenHandler.exit(); else fullScreenHandler.enter();
    } else {
      window.scrollBy(0,1000);
    }

  };

  const handleDisplayControls = () => {
    timersRef.current.map((t) => clearTimeout(t));
    setShowControls(true);
    setShowNavigation(true);
    setShowActions(true);
    setShowFragments(true);

    const timer = setTimeout(() => {
      setShowControls(false);
      setShowNavigation(false);
      setShowActions(false);
      setShowFragments(false);
    }, 5000);

    timersRef.current.push(timer);
  };

  const handleFragmentSelection = (id) => {
    setCurrentFragmentID(id);
    // setShowControls(false);
    // setShowActions(false);
    // setShowFragments(false);
  };

  return (
      <div className="interactive-player">
        <FullScreen
            handle={fullScreenHandler}
            onChange={(status) => setFullScreen(status)}
        >
          <div className="interactive-player__body full-screenable-node" onMouseMove={handleDisplayControls}>
            <div className="interactive-player__header">
              <NavBar />
            </div>
            <video
                style={{pointerEvents: 'none'}}
                onPlay={handlePlay}
                onEnded={handleEndOfVideo}
                playsInline
                ref={videoRef}
                //autoPlay={true}
                src=""
                poster=""
                width="100%"
                height="100%"
                controls={false}
                controlsList={'nodownload nofullscreen'}
                disablePictureInPicture={true}
                className="interactive-player__body__video"
                onLoadedMetadata={handleLoadedMetadata}
                onCanPlay={() => setIsBuffering(false)}
            />
            { isAutoPlaying && isBuffering && <Loader /> }
            {
              !isAutoPlaying &&
              <a onClick={handlePlay} className="interactive-player__playBtn anchor"><FontAwesomeIcon icon={faPlay} /></a>
            }
          </div>

          { isPlayerReady && /*showControls &&*/ <>
              <div className={`interactive-player__body__player-buttons ${showControls ? '' :'mouse--still' }`}>
                {currentScene.skipToSex && <a href="#" onClick={handleGoToSexScene} className="button button-outline-secondary">SKIP TO SEX</a> }
                <Fragments fragments={currentScene.fragments} onFragmentSelection={handleFragmentSelection}/>
              </div>
              <div className="interactive-player__body__player-actions">
                { currentScene?.fragments[currentFragmentID]?.actions &&
                  <Actions actions={currentScene.fragments[currentFragmentID].actions}
                           onActionSelection={(id) => handleActionPlay(id)}/>
                }
              </div>
              <div className="interactive-player____body__player-controls">
                <Controls isFullScreen={isFullScreen} videoRef={videoRef} handleFullscreen={handleFullscreen}/>
              </div>
            </>
          }
          {currentScreen === SELECTION_SCREEN && <SceneSelection />}
        </FullScreen>
      </div>
  );
}

export default Scene;
